import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore, useEffectiveRole } from '@/store/auth'
import { authApi } from '@/api/auth'
import { usersApi } from '@/api/users'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { LayoutDashboard, FolderKanban, Users, LogOut, Eye, Settings, HelpCircle, Sparkles, Pencil, Check, X, ScrollText, BookOpen } from 'lucide-react'
import clsx from 'clsx'
import type { Role } from '@/types'
import ChangePasswordModal from '@/components/auth/ChangePasswordModal'
import OnboardingWizard from '@/components/common/OnboardingWizard'
import WhatsNewTour from '@/components/common/WhatsNewTour'

const navItems = [
  { to: '/dashboard', label: 'Главный экран', icon: LayoutDashboard, directorOnly: false },
  { to: '/projects',  label: 'Проекты',       icon: FolderKanban,   directorOnly: false },
  { to: '/users',     label: 'Сотрудники',    icon: Users,           directorOnly: true },
  { to: '/staff',     label: 'Сотрудники',    icon: Users,           gipOnly: true },
  { to: '/audit',     label: 'Журнал',        icon: ScrollText,      directorOnly: true },
  { to: '/help',      label: 'Помощь',        icon: BookOpen,        directorOnly: false },
]

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: 'director',        label: 'Директор (Вы)' },
  { value: 'deputy_director', label: 'Зам. директора' },
  { value: 'chief_engineer',  label: 'ГИП' },
  { value: 'designer',        label: 'Проектировщик' },
]

export default function Layout() {
  const { user, logout, previewRole, setPreviewRole, setUser } = useAuthStore()
  const effectiveRole = useEffectiveRole()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [showWhatsNew, setShowWhatsNew] = useState(false)
  const [editingSelfName, setEditingSelfName] = useState(false)
  const [selfNameValue, setSelfNameValue] = useState('')

  const selfNameMutation = useMutation({
    mutationFn: (full_name: string) => usersApi.patch(user!.id, { full_name }),
    onSuccess: (res) => {
      setUser({ ...user!, full_name: res.data.full_name })
      qc.invalidateQueries({ queryKey: ['users'] })
      setEditingSelfName(false)
    },
  })

  const isRealDirector = user?.role === 'director'
  const isPreviewing   = isRealDirector && previewRole !== null && previewRole !== 'director'

  const isDirectorFamily =
    effectiveRole === 'director' || effectiveRole === 'deputy_director'

  const visibleNav = navItems.filter((item) => {
    if ('gipOnly' in item && item.gipOnly) return effectiveRole === 'chief_engineer'
    return !('directorOnly' in item && item.directorOnly) || isDirectorFamily
  })

  async function handleLogout() {
    await authApi.logout().catch(() => {})
    logout()
    navigate('/login')
  }

  function handleRoleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const val = e.target.value as Role
    setPreviewRole(val === 'director' ? null : val)
    // При смене роли переходим на «безопасную» страницу
    navigate('/projects')
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="flex w-56 flex-col border-r border-gray-200 bg-white">
        <div className="flex h-14 items-center border-b border-gray-200 px-4">
          <span className="font-bold text-brand-600 text-sm leading-tight">
            СУП<br />
            <span className="text-xs font-normal text-gray-500">ГрандГеоПроект</span>
          </span>
        </div>

        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {visibleNav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-tour={`nav-${to.slice(1)}`}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                )
              }
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Переключатель ролей — только для директора */}
        {isRealDirector && (
          <div className={clsx(
            'mx-2 mb-2 rounded-lg border px-3 py-2.5 text-xs',
            isPreviewing
              ? 'border-amber-300 bg-amber-50'
              : 'border-gray-200 bg-gray-50',
          )}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Eye className={clsx('h-3.5 w-3.5', isPreviewing ? 'text-amber-600' : 'text-gray-400')} />
              <span className={clsx('font-medium', isPreviewing ? 'text-amber-700' : 'text-gray-500')}>
                Просмотр от имени
              </span>
            </div>
            <select
              value={previewRole ?? 'director'}
              onChange={handleRoleChange}
              className={clsx(
                'block w-full rounded-md border px-2 py-1 text-xs focus:outline-none focus:ring-1',
                isPreviewing
                  ? 'border-amber-300 bg-white text-amber-800 focus:ring-amber-400 focus:border-amber-400'
                  : 'border-gray-300 bg-white text-gray-700 focus:ring-brand-500 focus:border-brand-500',
              )}
            >
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* User info + gear dropdown + logout */}
        <div className="border-t border-gray-200 p-3">
          <div className="flex items-center justify-between mb-2 px-1">
            <div className="min-w-0 flex-1">
              {/* Имя с inline-редактированием */}
              {editingSelfName ? (
                <div className="flex items-center gap-1">
                  <input
                    autoFocus
                    value={selfNameValue}
                    onChange={(e) => setSelfNameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') selfNameMutation.mutate(selfNameValue)
                      if (e.key === 'Escape') setEditingSelfName(false)
                    }}
                    className="w-full rounded border border-brand-400 px-1.5 py-0.5 text-xs font-medium text-gray-900 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                  <button
                    onClick={() => selfNameMutation.mutate(selfNameValue)}
                    disabled={selfNameMutation.isPending}
                    className="flex-shrink-0 text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                    title="Сохранить"
                  >
                    <Check className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => setEditingSelfName(false)}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600"
                    title="Отмена"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1 group">
                  <div className="text-xs font-medium text-gray-900 truncate">
                    {user?.full_name ?? user?.email}
                  </div>
                  <button
                    onClick={() => { setSelfNameValue(user?.full_name ?? ''); setEditingSelfName(true) }}
                    className="flex-shrink-0 text-gray-300 opacity-0 group-hover:opacity-100 hover:text-brand-600 transition-all"
                    title="Изменить имя"
                  >
                    <Pencil className="h-2.5 w-2.5" />
                  </button>
                </div>
              )}
              {/* Метка роли */}
              <div className="text-[11px] text-brand-600 font-medium truncate mt-0.5">
                {user?.role === 'director' ? 'Администратор системы' :
                 user?.role === 'deputy_director' ? 'Зам. директора' :
                 user?.role === 'chief_engineer' ? 'ГИП' : 'Проектировщик'}
              </div>
              <div className="text-xs text-gray-400 truncate">{user?.email}</div>
            </div>
            <div className="relative flex-shrink-0 ml-2 flex items-center gap-1">
              {/* Кнопка «?» — интерактивное обучение */}
              <button
                onClick={() => setShowOnboarding(true)}
                className="rounded-lg p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                title="Интерактивное обучение"
              >
                <HelpCircle className="h-4 w-4" />
              </button>
              {/* Кнопка «✨» — что нового после обновления */}
              <button
                onClick={() => setShowWhatsNew(true)}
                className="rounded-lg p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                title="Что нового в системе"
              >
                <Sparkles className="h-4 w-4" />
              </button>
              <button
                onClick={() => setShowUserMenu((v) => !v)}
                className="rounded-lg p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                title="Настройки профиля"
              >
                <Settings className="h-4 w-4" />
              </button>
              {showUserMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} />
                  <div className="absolute bottom-8 right-0 z-20 w-44 rounded-lg bg-white border border-gray-200 shadow-lg py-1">
                    <button
                      onClick={() => { setShowUserMenu(false); setShowChangePassword(true) }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Settings className="h-4 w-4 text-gray-400" />
                      Сменить пароль
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      Выйти
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          {/* Резервная кнопка Выйти */}
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Выйти
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        {/* Баннер режима предпросмотра */}
        {isPreviewing && (
          <div className="sticky top-0 z-40 flex items-center gap-2 bg-amber-400 px-4 py-2 text-sm font-medium text-amber-900 shadow-sm">
            <Eye className="h-4 w-4 flex-shrink-0" />
            <span>
              Предпросмотр: вы видите интерфейс как{' '}
              <strong>{ROLE_OPTIONS.find((o) => o.value === previewRole)?.label}</strong>.
              API-запросы идут от вашего аккаунта директора.
            </span>
            <button
              onClick={() => { setPreviewRole(null); navigate('/dashboard') }}
              className="ml-auto flex-shrink-0 rounded bg-amber-300 px-2 py-0.5 text-xs font-semibold hover:bg-amber-200 transition-colors"
            >
              Выйти из предпросмотра
            </button>
          </div>
        )}
        <Outlet />
      </main>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}

      {/* Интерактивное обучение — автопоказ при первом входе + по кнопке «?» */}
      {user && (
        <OnboardingWizard
          userId={user.id}
          role={user.role as Role}
          forceShow={showOnboarding}
          onClose={() => setShowOnboarding(false)}
        />
      )}
      {user && (
        <WhatsNewTour
          userId={user.id}
          role={effectiveRole as Role}
          autoAllowed={!isPreviewing}
          forceShow={showWhatsNew}
          onClose={() => setShowWhatsNew(false)}
        />
      )}
    </div>
  )
}
